import { usePageNavigation } from '@shared/components/navigation';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { Button } from '@shared/components/ui/button';
import { CodeLabelSize, generateCodeLabelsPdf } from '@shared/utils/codeLabelPdf';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { usePackingStationsMain } from '../../hooks/usePackingStationsMain';
import { PackingStationCardList, PackingStationsAgGridTable, PackingStationsStats } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { FiltersSection } from '../interaction';
import type { PackingStation, PackingStationRelationsTabKey } from '../../models/PackingStation';
import { CreatePackingStationModal, CreatePackingStationModalRef } from '../modals/CreatePackingStationModal';
import { DetailModal, DetailModalRef } from '../modals/DetailModal';

export const Main = () => {
  const { t } = useTranslation('packingStationsModule');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const detailModalRef = useRef<DetailModalRef>(null);
  const createModalRef = useRef<CreatePackingStationModalRef>(null);

  const {
    selectedColumns,
    packingStations,
    isLoading,
    isFetching,
    error,
    total,
    tableProps,
    handleCodeFilterChange,
    handleActiveFilterChange,
    handleColumnsChange,
    clearFilters,
    refetch,
    isFetchingNextPage,
    hasActiveFilters,
    filterCode,
    filterActive,
  } = usePackingStationsMain();

  // Columnas para el ColumnSelector (formato TanStack)
  const { tanStackColumns } = useAgGridColumns({ selectedColumns });
  const selectorColumns = useMemo(() => tanStackColumns, [tanStackColumns]);

  const handleOpenCreate = useCallback(() => {
    createModalRef.current?.openCreate();
  }, []);

  const handleOpenEdit = useCallback((station: PackingStation) => {
    createModalRef.current?.openEdit(station);
  }, []);

  const breadcrumbs = useMemo(() => [{ label: t('title'), path: '/packaging/packing-stations' }], [t]);

  const headerActions = useMemo(
    () => [
      {
        id: 'create-packing-station',
        label: t('buttons.create'),
        onClick: handleOpenCreate,
        variant: 'primary' as const,
      },
    ],
    [handleOpenCreate, t],
  );

  usePageNavigation({ breadcrumbs, headerActions });

  // Marcar fin de la carga inicial
  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const handleOpenDetail = useCallback((station: PackingStation, tab?: PackingStationRelationsTabKey) => {
    detailModalRef.current?.open(station, tab);
  }, []);

  const handlePrintStation = useCallback(async (station: PackingStation, size: CodeLabelSize) => {
    try {
      await generateCodeLabelsPdf({
        labels: [{ code: station.code }],
        labelSize: size,
        fileName: `etiqueta_puesto_${station.code}_${Date.now()}.pdf`,
      });
    } catch (error) {
      console.error('Error generando PDF para puesto de packing:', error);
    }
  }, []);

  // Spinner sólo en primera carga
  if (isInitialLoad && isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error as Error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      {/* Estadísticas derivadas de las filas cargadas en la tabla */}
      <PackingStationsStats packingStations={packingStations} isLoading={isFetching && packingStations.length === 0} />

      {/* Header con filtros */}
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection
              onCodeFilterChange={handleCodeFilterChange}
              onActiveFilterChange={handleActiveFilterChange}
              onClearFilters={clearFilters}
              filterCode={filterCode}
              filterActive={filterActive}
              columns={selectorColumns}
              selectedColumns={selectedColumns}
              onColumnsChange={handleColumnsChange}
              hasActiveFilters={hasActiveFilters}
            />
          </div>
          <TotalResults total={total} />
        </div>
      </div>

      {/* Vista desktop/mobile de la lista */}
      <div
        className={`transition-all duration-300 mb-4 ${
          packingStations.length > 0 || hasActiveFilters ? 'opacity-100 h-auto' : 'opacity-0 h-0 overflow-hidden'
        } ${isFetching && !isFetchingNextPage ? 'opacity-75' : ''}`}
      >
        {isMobile ? (
          <PackingStationCardList
            packingStations={packingStations}
            fetchNextPage={tableProps.fetchNextPage}
            hasNextPage={tableProps.hasNextPage}
            isFetchingNextPage={tableProps.isFetchingNextPage}
            onOpenDetail={handleOpenDetail}
            onOpenEdit={handleOpenEdit}
            onPrint={handlePrintStation}
            refetch={refetch}
          />
        ) : (
          <PackingStationsAgGridTable
            {...tableProps}
            onOpenDetail={handleOpenDetail}
            onOpenEdit={handleOpenEdit}
            onPrint={handlePrintStation}
            refetch={refetch}
          />
        )}
      </div>

      {/* Estado vacío */}
      {packingStations.length === 0 && (
        <EmptyView
          type={hasActiveFilters ? 'noResults' : 'emptyState'}
          icon={EMPTY_VIEW_ICONS.DEFAULT}
          customAction={
            hasActiveFilters ? null : (
              <Button
                variant="primary"
                size="default"
                onClick={handleOpenCreate}
                data-testid="create-packing-station-empty"
              >
                {t('buttons.create')}
              </Button>
            )
          }
        />
      )}

      <CreatePackingStationModal ref={createModalRef} onSuccess={refetch} />

      {/* Modal de detalle (Impresoras / Clientes / Ubicaciones) */}
      <DetailModal ref={detailModalRef} onRefetch={refetch} />
    </div>
  );
};
